import { Consent } from '@onbeam/utils';
import { z } from 'zod';
import { CookieConsentProps } from './CookieConsent.types';
import { link } from '@onbeam/styled-system/patterns';

export type ConsentOption =
  | keyof Omit<Consent, 'ad_personalization' | 'ad_storage' | 'ad_user_data'>
  | 'ad_consent';

export const consentOptions: {
  key: ConsentOption;
  isDisabled: boolean;
}[] = [
  { key: 'ad_consent', isDisabled: false },
  { key: 'analytics_storage', isDisabled: false },
  { key: 'functionality_storage', isDisabled: true },
];

export const consentFormSchema = z.object({
  ad_consent: z.boolean(),
  analytics_storage: z.boolean(),
  functionality_storage: z.literal(true),
}) satisfies z.ZodObject<{
  [key in ConsentOption]: z.ZodBoolean | z.ZodLiteral<true>;
}>;

export const convertConsentToFormValues = (
  consent: Consent,
): {
  ad_consent: boolean;
  analytics_storage: boolean;
  functionality_storage: true;
} => ({
  ad_consent:
    consent.ad_personalization && consent.ad_storage && consent.ad_user_data,
  analytics_storage: consent.analytics_storage,
  functionality_storage: true,
});

export const DEFAULT_FORM_VALUES = {
  ad_consent: false,
  analytics_storage: false,
  functionality_storage: true,
} satisfies Record<ConsentOption, boolean>;

export const defaultProps: Partial<CookieConsentProps> = {
  i18n: {
    title: 'Cookie settings',
    description:
      'We use essential functionality cookies to make the site work. If you accept, we may also use cookies to enhance your experience, measure ad performance and analyze traffic. You can change your cookie settings by clicking “Manage”.',
    modalDescription: (
      <>
        We use different types of cookies on our website. Note that we do not
        sell your personal information. You can change your preferences at any
        time. Read more about how we use cookies in our{' '}
        <a
          className={link()}
          href="https://onbeam.com/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        .
      </>
    ),
    saveLabel: 'save and close',
    acceptLabel: 'accept',
    rejectLabel: 'reject',
    manageLabel: 'manage',
    options: {
      ad_consent: {
        title: 'Advertising cookies',
        description: (
          <>
            We use Google Adsense conversion tracking to help measure how our
            ads on other platforms perform and how users interact with our site
            through them. They allow us to see how many users visit after
            clicking on an ad and which pages they engage with. To measure the
            effectiveness of these ads, Google Adsense conversion tracking may
            set cookies or track interactions. You can also adjust your ad
            settings with Google{' '}
            <a
              className={link()}
              href="https://myadcenter.google.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              here
            </a>
            .
          </>
        ),
      },
      analytics_storage: {
        title: 'Analytics storage',
        description:
          'Analytics storage helps us understand how you use our website, which allows us to improve the user experience and optimize our services.',
      },
      functionality_storage: {
        title: 'Functionality storage',
        description:
          'Functionality storage is necessary for the basic functioning of the website and cannot be disabled.',
      },
    },
  },
};
